import { useCallback, useEffect, useMemo } from "react";
import { Alert, Flex, Skeleton, Typography } from "antd";
import { CustomButton, WhiteBoxBlock } from "ui";
import { observer } from "mobx-react-lite";
import styles from "./MessageTemplates.module.css";
import { useStores } from "stores";
import { SMSEditModal } from "./SMSEditModal";
import { MailEditModal } from "./MailEditModal";
import { SMS_TYPE_TEXT, SmsTemplateType } from "interfaces/templates";
import { DataStoreConstructor } from "stores/core/DataStoreConstructor";
import { getSMSTemplate, resetMailLayout, resetSMSTemplate } from "services/settingsService";

import { ReactComponent as RotateIcon } from "images/rotate.svg";
import { ConfirmBtn } from "components";

type SmsTemplateDto = Awaited<ReturnType<typeof getSMSTemplate>>;

const defaultSmsTemplate: SmsTemplateDto = {
  loginTemplate: "",
  legallySignificantActionTemplate: "",
  commonActionTemplate: "",
};

interface TemplateInputProps {
  template: string;
  label: string;
  type: SmsTemplateType;
  onRefresh: () => void;
}

const TemplateInput = ({ template, label, type, onRefresh }: TemplateInputProps) => {
  const { actionModals } = useStores();

  const handleEditTemplate = useCallback(() => {
    actionModals.open(
      () => <SMSEditModal template={template} type={type} onClose={actionModals.closeAll} onRefresh={onRefresh} />,
      {
        width: "700px",
        title: "Редактировать шаблон SMS",
        onClose: actionModals.closeAll,
        isOverlayClose: true,
      }
    );
  }, [template, type, actionModals, onRefresh]);

  return (
    <Flex gap={8} align="center">
      <Flex vertical gap={"2px"} className={styles.templateCard}>
        <span className="text-hint">{label}</span>
        <Typography.Text>{template}</Typography.Text>
      </Flex>
      <CustomButton size="xlarge" onClick={handleEditTemplate}>
        Редактировать
      </CustomButton>
      <ConfirmBtn
        calledQuery={() => resetSMSTemplate(type)}
        text={
          <Typography.Text>
            Вы уверены, что хотите сбросить шаблон {<Typography.Text strong>{SMS_TYPE_TEXT[type]}</Typography.Text>} до
            шаблона по умолчанию?
          </Typography.Text>
        }
        successText={`Шаблон ${SMS_TYPE_TEXT[type]} успешно сброшен`}
        btnProps={{
          icon: <RotateIcon className={styles.rotateIcon} />,
        }}
        modalProps={{
          width: "600px",
          title: "Сбросить данные шаблона",
          onSuccess: () => setTimeout(() => onRefresh(), 1000),
        }}
        tooltip="Сбросить данные шаблона"
      />
    </Flex>
  );
};

const EmailTemplates = observer(() => {
  const { actionModals } = useStores();

  const handleEditMailLayout = useCallback(() => {
    actionModals.open(() => <MailEditModal onClose={actionModals.closeAll} />, {
      width: "700px",
      title: "Редактировать шаблон Email",
      onClose: actionModals.closeAll,
      isOverlayClose: true,
    });
  }, [actionModals]);

  return (
    <Flex vertical gap="20px" className="margin-top-large">
      <Flex vertical gap="12px">
        <Typography.Title level={4} className="margin-top-none margin-bottom-none">
          Email-сообщения
        </Typography.Title>
        <Typography.Text>Меняйте шаблон Email писем</Typography.Text>
      </Flex>
      <Flex gap={8}>
        <CustomButton size="xlarge" onClick={handleEditMailLayout}>
          Редактировать
        </CustomButton>
        <ConfirmBtn
          calledQuery={() => resetMailLayout()}
          text="Вы уверены, что хотите сбросить шаблон сообщения на почту до шаблона по умолчанию?"
          successText="Шаблон сообщения на почту успешно сброшен"
          btnProps={{
            icon: <RotateIcon className={styles.rotateIcon} />,
          }}
          tooltip="Сбросить данные шаблона"
          modalProps={{
            width: "600px",
            title: "Сбросить данные шаблона",
          }}
        />
      </Flex>
    </Flex>
  );
});

export const MessageTemplates = observer(() => {
  const smsTemplateStore = useMemo(
    () =>
      new DataStoreConstructor<SmsTemplateDto>({
        fetchMethod: getSMSTemplate,
        defaultData: defaultSmsTemplate,
      }),
    []
  );

  const { data, statuses, fetch } = smsTemplateStore;

  useEffect(() => {
    fetch();
  }, [fetch]);

  if (statuses.loading) {
    return <Skeleton active />;
  }

  const { loginTemplate, legallySignificantActionTemplate, commonActionTemplate } = data || {};

  return (
    <>
      <Typography.Title>Шаблоны сообщений</Typography.Title>
      <WhiteBoxBlock className={styles.templatesBox}>
        <Flex vertical gap="large">
          <Flex vertical gap="12px">
            <Typography.Title level={4} className="margin-top-none margin-bottom-none">
              SMS-сообщения
            </Typography.Title>
            <Typography.Text>Меняйте шаблон SMS-оповещений</Typography.Text>
          </Flex>
          {statuses.fetchError ? (
            <Alert message="Ошибка при загрузке данных" type="error" />
          ) : (
            <Flex vertical gap="middle">
              <TemplateInput
                label={"Авторизация"}
                template={loginTemplate || ""}
                type={SmsTemplateType.LOGIN}
                onRefresh={fetch}
              />
              <TemplateInput
                label={"Юридически значимые действия"}
                template={legallySignificantActionTemplate || ""}
                type={SmsTemplateType.LEGALLY_SIGNIFICANT_ACTION}
                onRefresh={fetch}
              />
              <TemplateInput
                label={"Обычные действия"}
                template={commonActionTemplate || ""}
                type={SmsTemplateType.COMMON_ACTION}
                onRefresh={fetch}
              />
            </Flex>
          )}
          <EmailTemplates />
        </Flex>
      </WhiteBoxBlock>
    </>
  );
});
